import { useMemo } from 'react'
import { useFlipFlowData } from './useFlipFlowData'
import { useFlipFeatureFlags } from './useFlipFeatureFlags'
import { useUserLocale } from './useUserLocale'
import { useShippingAddress } from '../stateData/hooks'

/**
 * List of locales eligible for enrollment without billing information.
 * These markets allow users to start service without payment details upfront.
 */
export const ENROLL_WITHOUT_BILLING_LOCALES = [
  'en_US',
  'en_GB',
  'en_CA',
  'en_AU',
  'en_NZ',
  'en_IE'
] as const

/**
 * Interface for billing validation results in Flip flows
 */
export interface FlipBillingValidation {
  canEnrollWithoutBilling: boolean
  requireBilling: boolean
  hasBilling: boolean
  stateBillingRequired: boolean
  shouldShowBillingForm: boolean
  shouldSkipBillingStep: boolean
  isExpressCheckoutEligible: boolean
  enrollWithoutBilling: boolean
  showPersonalizedOnboardingContent: boolean
  hasInsufficientPrepaid: boolean
  missingRequiredPayment: boolean
  addBillingAppearance: 'primary' | 'secondary'
}

/**
 * Custom hook for centralized billing validation logic in Flip flows.
 * Consolidates billing requirements, locale eligibility, and feature flag combinations
 * to provide consistent validation across all Flip components.
 *
 * @returns {FlipBillingValidation} Comprehensive billing validation flags and states
 *
 * @example
 * ```typescript
 * const {
 *   canEnrollWithoutBilling,
 *   shouldShowBillingForm,
 *   missingRequiredPayment
 * } = useFlipBillingValidation()
 *
 * if (missingRequiredPayment) {
 *   // Show error or block enrollment
 * }
 * ```
 */
export const useFlipBillingValidation = (): FlipBillingValidation => {
  const {
    requireBilling,
    hasBilling,
    hasShipping,
    settings,
    benefits
  } = useFlipFlowData()

  const {
    enableEnrollWithoutBillingForLocales,
    enablePersonalizedOnboardingForFlip,
    enableExpressCheckout,
    enableExpressCheckoutEasyEnroll
  } = useFlipFeatureFlags()

  const { country, language } = useUserLocale()
  const shippingAddressState = useShippingAddress()

  return useMemo(() => {
    const currentLocale = `${language}_${country?.toUpperCase() ?? ''}`

    const isLocaleEligible = ENROLL_WITHOUT_BILLING_LOCALES.includes(
      currentLocale as typeof ENROLL_WITHOUT_BILLING_LOCALES[number]
    )

    const shippingState = shippingAddressState.data?.state
    const flipBillingRequiredStates = settings?.flipBillingRequiredStates ?? []

    const isStateBillingRequired = enableEnrollWithoutBillingForLocales
      ? false
      : flipBillingRequiredStates.includes(shippingState ?? '')

    const canEnrollWithoutBilling =
      enableEnrollWithoutBillingForLocales && isLocaleEligible

    const shouldShowBillingForm =
      !canEnrollWithoutBilling || isStateBillingRequired || requireBilling

    const shouldSkipBillingStep =
      canEnrollWithoutBilling && !isStateBillingRequired && !requireBilling

    const hasExpressCheckoutFlags =
      enableExpressCheckout || enableExpressCheckoutEasyEnroll
    const isExpressCheckoutEligible =
      hasExpressCheckoutFlags &&
      (canEnrollWithoutBilling || !requireBilling) &&
      hasShipping

    const enrollWithoutBilling =
      enablePersonalizedOnboardingForFlip &&
      enableEnrollWithoutBillingForLocales &&
      !(hasBilling && hasShipping)

    const showPersonalizedOnboardingContent =
      enablePersonalizedOnboardingForFlip &&
      !hasBilling &&
      hasShipping &&
      !isStateBillingRequired

    const prepaidBalanceCents = benefits?.prepaidBalanceCents ?? 0
    const hasInsufficientPrepaid =
      prepaidBalanceCents > 0 && !hasBilling && requireBilling

    const missingRequiredPayment = !hasBilling && requireBilling

    const addBillingAppearance: 'primary' | 'secondary' =
      isStateBillingRequired || requireBilling ? 'primary' : 'secondary'

    return {
      canEnrollWithoutBilling,
      requireBilling,
      hasBilling,
      stateBillingRequired: isStateBillingRequired,

      shouldShowBillingForm,
      shouldSkipBillingStep,

      isExpressCheckoutEligible,

      enrollWithoutBilling,
      showPersonalizedOnboardingContent,

      hasInsufficientPrepaid,
      missingRequiredPayment,

      addBillingAppearance
    }
  }, [
    requireBilling,
    hasBilling,
    hasShipping,
    settings,
    benefits,
    enableEnrollWithoutBillingForLocales,
    enablePersonalizedOnboardingForFlip,
    enableExpressCheckout,
    enableExpressCheckoutEasyEnroll,
    country,
    language,
    shippingAddressState.data?.state
  ])
}

/**
 * Simplified helper hook to quickly check if user can enroll.
 * Useful for quick conditional checks without needing full validation context.
 *
 * @returns {boolean} True if user can proceed with enrollment
 *
 * @example
 * ```typescript
 * const canEnroll = useCanEnroll()
 *
 * if (!canEnroll) {
 *   return <BlockedEnrollmentMessage />
 * }
 * ```
 */
export const useCanEnroll = (): boolean => {
  const {
    requireBilling,
    hasBilling,
    missingRequiredPayment
  } = useFlipBillingValidation()

  return useMemo(() => {
    return !requireBilling || hasBilling || !missingRequiredPayment
  }, [requireBilling, hasBilling, missingRequiredPayment])
}
